/**
 * "Teri answered in <chat>" — the nudge for a reply that finished somewhere the
 * member isn't looking.
 *
 * Deliberately NOT a `react-hot-toast`: there is no app-level `<Toaster/>` in
 * this SPA (pages mount their own), so a toast raised from Teri would render
 * nowhere on most routes. This rides along with Teri instead, which is
 * app-level by design — and it stays put until it is acted on or dismissed,
 * because the member who walked away is exactly the one who would miss a
 * three-second toast.
 */
import React from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { CheckCircleIcon, ExclamationCircleIcon } from '@heroicons/react/24/solid';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { DURATION_NORMAL, EASE_OUT } from '@/lib/motion';
import { useAssistant } from '@/contexts/AssistantContext';

interface AnsweredNoticeProps {
  /** `pill` sits beside the blop; `banner` sits inside the panel. */
  variant: 'pill' | 'banner';
}

const AnsweredNotice: React.FC<AnsweredNoticeProps> = ({ variant }) => {
  const { answeredNotice, openAnsweredNotice, dismissAnsweredNotice } = useAssistant();
  const reduceMotion = useReducedMotion();
  const failed = answeredNotice?.status === 'failed';
  const Icon = failed ? ExclamationCircleIcon : CheckCircleIcon;

  return (
    <AnimatePresence>
      {answeredNotice && (
        <motion.div
          initial={reduceMotion ? { opacity: 0 } : { opacity: 0, y: 6, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={reduceMotion ? { opacity: 0 } : { opacity: 0, y: 6, scale: 0.97 }}
          transition={{ duration: DURATION_NORMAL, ease: EASE_OUT }}
          role="status"
          data-testid="teri-answered-notice"
          className={
            variant === 'pill'
              ? // Capped against the viewport, not just at 320px: the row it
                // sits in is anchored to the right edge and would otherwise
                // run off the left one on a phone.
                'flex items-center gap-1.5 max-w-[min(320px,calc(100vw-7.5rem))] px-3 py-2 rounded-full bg-surface-elevated border border-border-primary/25 shadow-lg'
              : 'mx-3 mb-2 flex items-center gap-1.5 px-3 py-2 rounded-xl bg-primary-500/10 border border-primary-500/20'
          }
        >
          <Icon
            className={`w-4 h-4 shrink-0 ${
              failed ? 'text-error-600' : 'text-primary-600 dark:text-primary-350'
            }`}
          />
          <p className="min-w-0 flex-1 text-[12px] leading-snug text-text-primary">
            {failed ? 'Teri hit a snag in' : 'Teri answered in'}{' '}
            <span className="font-semibold">“{answeredNotice.title}”</span>
          </p>
          <button
            type="button"
            onClick={openAnsweredNotice}
            className="shrink-0 px-2 py-0.5 rounded-full text-[12px] font-bold text-primary-600 dark:text-primary-350 hover:bg-primary-500/10 transition-colors duration-fast focus-ring"
          >
            Open
          </button>
          <button
            type="button"
            onClick={dismissAnsweredNotice}
            aria-label="Dismiss"
            className="shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-text-tertiary hover:text-text-primary hover:bg-text-tertiary/10 transition-colors duration-fast focus-ring"
          >
            <XMarkIcon className="w-3.5 h-3.5" />
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default AnsweredNotice;
